import { Fragment, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import { MdcSnackbar } from 'ids-wc/dist/react/components';
import { selectSnackbarMessages } from '@shared/store/ui/ui.selectors';
import { UIActions } from '@shared/store/ui/ui';
import styles from './Snackbar.module.css';
import type { AppDispatch } from '@shared/store';

export const Snackbar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const messages = useSelector(selectSnackbarMessages);
  const snackbarRefs = useRef<Map<string, HTMLMdcSnackbarElement>>(new Map());

  useEffect(() => {
    messages.forEach((message) => {
      const snackbarElement = snackbarRefs.current.get(message.id);
      if (snackbarElement && snackbarElement.open) {
        snackbarElement.open();

        if (message.duration && message.duration > 0) {
          setTimeout(() => {
            dispatch(UIActions.hideSnackbar(message.id));
          }, message.duration);
        }
      }
    });
  }, [messages, dispatch]);

  const handleClose = (messageId: string) => {
    dispatch(UIActions.hideSnackbar(messageId));
  };

  const setSnackbarRef = (element: HTMLMdcSnackbarElement, messageId: string) => {
    if (element) {
      snackbarRefs.current.set(messageId, element);
    } else {
      snackbarRefs.current.delete(messageId);
    }
  };

  return createPortal(
    <Fragment>
      {messages.map((message) => (
        <MdcSnackbar
          key={message.id}
          ref={(el) => setSnackbarRef(el as HTMLMdcSnackbarElement, message.id)}
          messageTitle={message.messageTitle}
          message={message.message}
          severity={message.severity}
          variant={message.variant || 'floating'}
          fixedPosition={message.fixedPosition}
          floatingPosition={message.floatingPosition || 'leading'}
          icon={message.icon}
          timeout={-1}
          onClick={() => handleClose(message.id)}
          hasClose
          className={styles.snackbar}
        />
      ))}
    </Fragment>,
    document.body
  );
};
